import { apiClient } from './api';
import type {
  CreatePraiseApiRequest,
  CreateFeedbackApiRequest,
  CreatePraiseResponse,
  CreateFeedbackResponse,
  CreateDebriefResponse,
  GetDebriefResponse,
  UpdateDebriefResponse,
  UpdateDebriefApiRequest,
  GetInteractionsByGoalIdResponse,
  UpdateFeedbackResponse,
  UpdateFeedbackApiRequest,
  GetInteractionsByIdsApiRequest,
  GetInteractionsByIdsResponse,
  GetAllMyInteractionsResponse,
  GetPraiseResponse,
  GetOpenBlockagesResponse,
  DebriefFormData,
  WizardAnswers,
  CreateDebriefApiRequestUI,
  CreateDebriefApiRequest,
  UpdateDebriefApiRequestUI,
  DebriefInteractionUI,
  PopulatedDebrief,
  PopulatedMatchPoints,
  EnrichInteractionResponse,
  EnrichInteractionApiRequest,
  CloseInteractionResponse,
  GetAllMyInteractionsApiRequest,
  UpdatePraiseApiRequest,
  UpdatePraiseResponse,
  UpdateActionPlanRequest,
  PopulatedFeedbackInteraction,
} from '@momentum/types';

export function convertDebriefFormData(
  formData: Partial<WizardAnswers<DebriefFormData>>
): PopulatedDebrief {
  // Collect all match point keys (dynamic)
  const matchPoints: PopulatedMatchPoints[] = [];

  const regex = /debrief-match-point-(\d+)-/;

  // get max index number from debrief-match-point
  const maxNumber = Object.keys(formData).reduce((max, str) => {
    const match = str.match(regex);
    const num = match ? Number(match[1]) : -1;
    return Math.max(max, num);
  }, -1);

  for (let i = 0; i <= maxNumber; i++) {
    const whatActions = formData[`debrief-match-point-${i}-what-actions`];
    const why = formData[`debrief-match-point-${i}-why`] || '';
    const assumption = formData[`debrief-match-point-${i}-assumption`];
    const reason = formData[`debrief-match-point-${i}-reason`] || ''; // Allow reason to be empty when user skips "why" step
    const signal = formData[`debrief-match-point-${i}-signal`];

    if (!whatActions || !assumption || !signal) {
      throw new Error('missing some values for match points...');
    }

    matchPoints.push({
      assumption,
      signal,
      reason,
      whatActions,
      why,
    });
  }

  const debrief: PopulatedDebrief = {
    ...formData,
    'debrief-match-points': matchPoints,
  };

  return debrief;
}

export const interactionsService = {
  // Create a new praise interaction
  async createPraise(
    data: CreatePraiseApiRequest
  ): Promise<CreatePraiseResponse> {
    return (
      await apiClient.post<CreatePraiseResponse>('/interactions/praise', data)
    ).data;
  },

  async getPraise(id: string): Promise<GetPraiseResponse | null> {
    return (
      await apiClient.get<GetPraiseResponse>(`/interactions/praise/${id}`)
    ).data;
  },

  async updatePraise(
    id: string,
    data: UpdatePraiseApiRequest
  ): Promise<UpdatePraiseResponse> {
    return (
      await apiClient.patch<UpdatePraiseResponse>(
        `/interactions/praise/${id}`,
        data
      )
    ).data;
  },

  // Create a new feedback interaction
  async createFeedback(
    data: CreateFeedbackApiRequest
  ): Promise<CreateFeedbackResponse> {
    return (
      await apiClient.post<CreateFeedbackResponse>(
        '/interactions/feedback',
        data
      )
    ).data;
  },

  async createDebrief(
    data: CreateDebriefApiRequestUI
  ): Promise<CreateDebriefResponse> {
    const newFormData: Record<string, PopulatedDebrief> = {};
    for (const creator in data.formData) {
      const creatorFormData = data.formData[creator];
      if (creatorFormData) {
        newFormData[creator] = convertDebriefFormData(creatorFormData);
      }
    }
    const apiData: CreateDebriefApiRequest = {
      ...data,
      formData: newFormData,
    };
    return (
      await apiClient.post<CreateDebriefResponse>(
        '/interactions/debrief',
        apiData
      )
    ).data;
  },

  async updateFeedback(
    id: string,
    data: UpdateFeedbackApiRequest
  ): Promise<UpdateFeedbackResponse> {
    return (
      await apiClient.patch<UpdateFeedbackResponse>(
        `/interactions/feedback/${id}`,
        data
      )
    ).data;
  },

  async getDebrief(id: string): Promise<DebriefInteractionUI> {
    const debrief = (
      await apiClient.get<GetDebriefResponse>(`/interactions/debrief/${id}`)
    ).data;

    const formData: DebriefInteractionUI['formData'] = {};

    for (const creator in debrief?.formData) {
      const formDataCreator = debrief?.formData?.[creator];
      if (!formDataCreator) continue;

      const creatorUIFormData: Partial<WizardAnswers<DebriefFormData>> = {
        ...formDataCreator,
      };

      formDataCreator?.['debrief-match-points']?.forEach((matchPoint, i) => {
        creatorUIFormData[`debrief-match-point-${i}-what-actions`] =
          matchPoint.whatActions;
        creatorUIFormData[`debrief-match-point-${i}-why`] = matchPoint.why;
        creatorUIFormData[`debrief-match-point-${i}-assumption`] =
          matchPoint.assumption;
        creatorUIFormData[`debrief-match-point-${i}-reason`] =
          matchPoint.reason;
        creatorUIFormData[`debrief-match-point-${i}-signal`] =
          matchPoint.signal;
      });

      formData[creator] = creatorUIFormData;
    }

    return {
      ...debrief,
      formData,
    };
  },

  async updateDebrief(
    id: string,
    data: UpdateDebriefApiRequestUI
  ): Promise<UpdateDebriefResponse> {
    const newFormData: Record<string, PopulatedDebrief> = {};
    for (const creator in data.formData) {
      const creatorFormData = data.formData[creator];
      if (creatorFormData) {
        newFormData[creator] = convertDebriefFormData(creatorFormData);
      }
    }
    const apiData: UpdateDebriefApiRequest = {
      ...data,
      formData: newFormData,
    };
    return (
      await apiClient.patch<UpdateDebriefResponse>(
        `/interactions/debrief/${id}`,
        apiData
      )
    ).data;
  },

  async closeDebrief(id: string): Promise<CloseInteractionResponse> {
    return (
      await apiClient.patch<CloseInteractionResponse>(
        `/interactions/close/${id}`
      )
    ).data;
  },

  async getInteractionsByGoalId(
    goalId: string
  ): Promise<GetInteractionsByGoalIdResponse> {
    return (
      await apiClient.get<GetInteractionsByGoalIdResponse>(
        `/interactions/goal/${goalId}`
      )
    ).data;
  },

  async getFeedback(id: string): Promise<PopulatedFeedbackInteraction | null> {
    return (
      await apiClient.get<PopulatedFeedbackInteraction>(
        `/interactions/feedback/${id}`
      )
    ).data;
  },

  async getInteractionsByIds(
    data: GetInteractionsByIdsApiRequest
  ): Promise<GetInteractionsByIdsResponse> {
    return (
      await apiClient.post<GetInteractionsByIdsResponse>(
        '/interactions/by-ids',
        data
      )
    ).data;
  },

  async getAllMyInteractions({
    limit,
  }: GetAllMyInteractionsApiRequest): Promise<GetAllMyInteractionsResponse> {
    const queryParams = new URLSearchParams();

    if (limit) queryParams.append('limit', limit.toString());

    const queryString = queryParams.toString();

    return (
      await apiClient.get<GetAllMyInteractionsResponse>(
        `/interactions/all-my-interactions${
          queryString ? `?${queryString}` : ''
        }`
      )
    ).data;
  },

  async getOpenBlockages(): Promise<GetOpenBlockagesResponse> {
    return (
      await apiClient.get<GetOpenBlockagesResponse>(
        '/interactions/open-blockages'
      )
    ).data;
  },

  async enrichFeedback(
    data: EnrichInteractionApiRequest
  ): Promise<EnrichInteractionResponse> {
    return (
      await apiClient.post<EnrichInteractionResponse>(
        '/interactions/enrich-interaction',
        data
      )
    ).data;
  },

  async updateActionPlan(
    id: string,
    data: UpdateActionPlanRequest
  ): Promise<CloseInteractionResponse> {
    return (
      await apiClient.patch<CloseInteractionResponse>(
        `/interactions/action-plan/${id}`,
        data
      )
    ).data;
  },
};
